// ReviewStep.jsx
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Button,
  Stack,
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
} from '@availity/element';

import { getFileExtIcon } from '../../../Common/utilities/util';

/**
 * ============================================================
 * Helpers (FHIR-aware, framework-agnostic)
 * ============================================================
 */

/**
 * getExt(item, url)
 * Safely returns the first extension on an item that matches a given URL.
 * Many FHIR UI hints (e.g., itemControl = page, help) are expressed as extensions.
 */
const getExt = (item, url) => (item && Array.isArray(item.extension) ? item.extension : []).find((e) => e.url === url);

/**
 * isPageGroup(item)
 * A "page" in the wizard is a group with itemControl.code === "page".
 * We only collect review fields grouped by these page-level items.
 * Returns false if the array contains an extension with url "http://elevancehealth.com/fhir/StructureDefinition/ui-review-step" and valueBoolean === true.
 */
const isPageGroup = (it) => {
  if (!it || it.type !== 'group') return false;

  // Check if the array contains the specific extension with valueBoolean === true
  const reviewStepExt = getExt(it, 'http://elevancehealth.com/fhir/StructureDefinition/ui-review-step');
  if (reviewStepExt?.valueBoolean === true) return false;

  // Check if the itemControl.code is "page"
  const ctrl = getExt(it, 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl');
  const code = ctrl?.valueCodeableConcept?.coding?.[0]?.code;
  return code === 'page';
};

/**
 * isDisplay(item)
 * Display-only elements (type "display") are non-inputs (help text, headings).
 * We skip these on the review surface.
 */
const isDisplay = (it) => it?.type === 'display';

/**
 * isEmpty(value)
 * Utility to decide whether a value is meaningfully empty, so we can hide it
 * on the review page (unless showEmpty=true).
 */
const isEmpty = (val) => {
  if (val == null) return true;
  if (Array.isArray(val)) return val.length === 0;
  const s = String(val).trim();
  return s.length === 0 || s === 'undefined' || s === 'null';
};

/**
 * collectFields(items, collector)
 * Depth-first traversal that pushes each *input* item (non-display) to a collector.
 * - Groups recurse into children
 * - Display items are skipped
 * Kept outside the component to satisfy lint rules (consistent-function-scoping).
 */
function collectFields(items, collector) {
  if (!Array.isArray(items)) return;
  for (const it of items) {
    if (!it) {
      // no-continue: do nothing (kept as an explicit branch for readability)
    } else if (it.type === 'group') {
      // Recursively collect child items for this group and attach as 'items' array
      const childFields = [];
      collectFields(it.item, (child) => {
        childFields.push(child);
      });
      collector({
        linkId: it.linkId,
        text: it.text !== undefined && it.text !== null ? it.text : it.linkId,
        type: 'group',
        repeats: !!it.repeats,
        items: childFields,
      });
    } else if (!isDisplay(it)) {
      collector({
        linkId: it.linkId,
        text: it.text !== undefined && it.text !== null ? it.text : it.linkId,
        type: it.type,
      });
    }
  }
}

/**
 * flattenFieldsByPage(questionnaire)
 * Organizes inputs by page (top-level group with itemControl = page).
 * Returns a shape consumable by the review UI:
 * {
 *   [pageLinkId]: {
 *     pageTitle: string,
 *     items: [{ linkId, text, type }]
 *   },
 *   ...
 * }
 */
const flattenFieldsByPage = (questionnaire, steps) => {
  const out = {};
  const top = Array.isArray(questionnaire?.item) ? questionnaire.item : [];
  // Use an inline arrow to satisfy unicorn/no-array-callback-reference
  const stepIds = new Set(steps.map((step) => step.id)); // Create a set of step IDs
  const pages = top.filter((it) => isPageGroup(it) && stepIds.has(it.linkId)); // Filter pages based on steps

  for (const page of pages) {
    const pageId = page.linkId;
    const fields = [];
    collectFields(page.item, (it) => {
      fields.push(it);
    });
    out[pageId] = { pageTitle: page.text || pageId, items: fields };
  }

  return out;
};

/**
 * ============================================================
 * ReviewStep Component
 * ============================================================
 * Renders a read-only summary of user-entered values, grouped by page.
 * - Pulls current form values via getValues() from react-hook-form
 * - Provides an "Edit" button for each page that routes back to that step
 * - Shows only fields with a value by default; can show empties with showEmpty
 *
 * Props:
 * - questionnaire: FHIR Questionnaire used to build steps/fields
 * - steps: [{ id, title, path, group }, ...] list from the wizard
 * - getValues: react-hook-form's getValues() (current snapshot of form data)
 * - showEmpty: boolean to show empty answers in the review (default false)
 * - onSubmit: final submit handler invoked by the "Submit" button
 */
export default function ReviewStep({
  questionnaire,
  steps,
  getValues, // from react-hook-form
  showEmpty = false,
}) {
  const navigate = useNavigate();

  // Snapshot of all current RHF values: { [linkId]: value }
  const values = getValues() || {};

  // Build the per-page field lists just once per questionnaire object identity
  const byPage = useMemo(() => flattenFieldsByPage(questionnaire, steps), [questionnaire, steps]);

  /**
   * pathForPage(pageId)
   * Resolve a page's router path (e.g., "/wizard/member-information") given
   * a page linkId. Used by the "Edit" button to jump back to that step.
   */
  const pathForPage = (pageId) => {
    for (const s of steps || []) {
      if (s.id === pageId) return s.path || '';
    }
    return '';
  };

  return (
    <Stack spacing={2} id="review-step-stack">
      {Object.entries(byPage).map(([pageId, payload]) => {
        const { pageTitle, items } = payload;
        // Recursive renderer for group and field items
        const renderItems = (items) =>
          items.map((item) => {
            if (item.type === 'group') {
              // Only display group header if at least one child item has a value
              if (item.repeats) {
                const repeatedGroups = [];
                for (let idx = 0; ; idx++) {
                  const suffix = idx === 0 ? '' : `-${idx}`;
                  // Check if any child value exists for this repeated group
                  const hasValue = item.items.some((child) => {
                    const childLinkId = `${child.linkId}${suffix}`;
                    const val = values[`${childLinkId}-start`] || values[`${childLinkId}-end`] || values[childLinkId];
                    return !isEmpty(val);
                  });
                  if (!hasValue) break;
                  repeatedGroups.push({ suffix, index: idx + 1 });
                }
                return repeatedGroups.map(({ suffix, index }) => {
                  // Only display header if at least one child has a value
                  const childItems = item.items.map((child) => ({
                    ...child,
                    linkId: `${child.linkId}${suffix}`,
                  }));
                  const hasChildData = childItems.some((child) => {
                    const val =
                      values[`${child.linkId}-start`] || values[`${child.linkId}-end`] || values[child.linkId];
                    return !isEmpty(val);
                  });
                  if (!hasChildData) return null;
                  return (
                    <React.Fragment key={`${item.linkId}${suffix}`}>
                      {item.text && (
                        <Grid size={{ xs: 12 }} id={`subgroup-header-${item.linkId}${suffix}`}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 1 }}
                            id={`subgroup-title-${item.linkId}${suffix}`}
                          >
                            {`${item.text} ${index}`}
                          </Typography>
                        </Grid>
                      )}
                      {renderItems(childItems)}
                    </React.Fragment>
                  );
                });
              }
              // Non-repeating group: Only display header if at least one child has a value
              const hasChildData = item.items.some((child) => {
                const val = values[`${child.linkId}-start`] || values[`${child.linkId}-end`] || values[child.linkId];
                return !isEmpty(val);
              });
              if (!hasChildData) return null;
              return (
                <React.Fragment key={item.linkId}>
                  {item.text && (
                    <Grid size={{ xs: 12 }} id={`subgroup-header-${item.linkId}`}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1 }}
                        id={`subgroup-title-${item.linkId}`}
                      >
                        {item.text}
                      </Typography>
                    </Grid>
                  )}
                  {renderItems(item.items)}
                </React.Fragment>
              );
            }

            const startDate = values[`${item.linkId}-start`]
              ? dayjs(values[`${item.linkId}-start`])?.format('MM/DD/YYYY')
              : '';

            const endDate = values[`${item.linkId}-end`]
              ? dayjs(values[`${item.linkId}-end`])?.format('MM/DD/YYYY')
              : '';

            const val =
              startDate || endDate
                ? `${startDate}${startDate && endDate ? ' - ' : ''}${endDate}`
                : dayjs.isDayjs(values[item.linkId])
                ? dayjs(values[item.linkId])?.format('MM/DD/YYYY')
                : values[item.linkId];
            if (!showEmpty && isEmpty(val)) return null;
            if (Array.isArray(val) && val.some((obj) => obj?.errorMessage)) return null; // Check for errorMessage
            if (Array.isArray(val) && val.some((obj) => obj?.file?.name)) {
              return (
                <React.Fragment key={item.linkId}>
                  <Grid size={{ xs: 12 }} id={`grid-label-${item.linkId}`}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} id={`typography-label-${item.linkId}`}>
                      {item.text}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }} id={`grid-value-${item.linkId}`}>
                    <List id={`attachment-list-${item.linkId}`}>
                      {val.map((uploadObject) => {
                        const attachment = uploadObject?.file || {};
                        if (uploadObject.errorMessage || !attachment.name) return null;
                        const Icon = getFileExtIcon(attachment.name);
                        return (
                          <ListItem id={`list-item-${attachment.attachmentId}`} key={attachment.attachmentId}>
                            <Grid
                              id={`file-row-${attachment.attachmentId}`}
                              container
                              spacing={2}
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                              flexWrap="wrap"
                            >
                              <Grid size="auto">
                                <ListItemIcon id={`file-icon-${attachment.attachmentId}`} sx={{ minWidth: '1.5rem' }}>
                                  <Icon />
                                </ListItemIcon>
                              </Grid>
                              <Grid size={11} sx={{ minWidth: '8rem' }}>
                                <ListItemText
                                  id={`file-name-${attachment.attachmentId}`}
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {attachment.name}
                                </ListItemText>
                              </Grid>
                            </Grid>
                            <Divider />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Grid>
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={item.linkId}>
                <Grid size={{ xs: 3 }} id={`grid-label-${item.linkId}`}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} id={`typography-label-${item.linkId}`}>
                    {item.text}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 3 }} id={`grid-value-${item.linkId}`}>
                  <Typography
                    variant="body2"
                    sx={{ opacity: isEmpty(val) ? 0.6 : 1, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    id={`typography-value-${item.linkId}`}
                  >
                    {isEmpty(val) ? '—' : String(val)}
                  </Typography>
                </Grid>
              </React.Fragment>
            );
          });

        return (
          <Card key={pageId} variant="elevation" id={`card-${pageId}`}>
            <CardHeader
              sx={{ borderBottom: 0, boxShadow: 'none' }}
              title={pageTitle}
              id={`card-header-${pageId}`}
              action={
                <Button
                  variant="text"
                  onClick={() => navigate(`/wizard/${pathForPage(pageId)}`)}
                  id={`edit-btn-${pageId}`}
                >
                  Edit
                </Button>
              }
            />
            <CardContent id={`card-content-${pageId}`}>
              <Grid container columnSpacing={1} rowSpacing={2} id={`grid-container-${pageId}`}>
                {renderItems(items)}
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

ReviewStep.propTypes = {
  questionnaire: PropTypes.shape({
    item: PropTypes.array,
  }).isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      path: PropTypes.string,
      group: PropTypes.object,
    })
  ).isRequired,
  getValues: PropTypes.func.isRequired,
  showEmpty: PropTypes.bool,
};
